import { AppContext } from 'App.context';
import React, { useContext, forwardRef, useImperativeHandle } from 'react';
import useDisableLicenceFormHook from './DisableLicenceForm.hook';
import { Checkbox, Row, Col, List, Input, Spin, Result, Popover } from 'antd';
import styles from './DisableLicenceForm.style';
import HotelSelector from 'Components/common/HotelSelector/HotelSelector';
import AppButton from 'Components/common/Forms/AppButton/AppButton';
import constants from '../../../../utils/constants';
import { getLicenceIconUrl } from '../utils';
import { InfoCircleOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';

const DisableLicenceForm = forwardRef((props, ref) => {
  const {
    appActions: { translate },
    appState,
  } = useContext(AppContext);

  const { state, actions, reasons, resetForm } = useDisableLicenceFormHook();

  useImperativeHandle(ref, () => ({
    resetForm,
  }));

  const currentStep = typeof state.step === 'number' ? state.step : 0;
  const steps = [
    translate('accountDetail.disableLicence.reasonStep.title'),
    translate('accountDetail.disableLicence.serviceSelectionStep.title'),
    translate('accountDetail.disableLicence.summaryStep.title'),
  ];

  const StepReason = (
    <form
      style={{
        width: '100%',
        minWidth: 400,
        maxWidth: 600,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
      }}
    >
      <label style={{ fontWeight: 500, marginBottom: 28 }}>
        {translate('accountDetail.disableLicence.reasonStep.subtitle')}
      </label>
      <Checkbox.Group
        options={reasons}
        value={state.selectedReasons}
        onChange={actions.handleReasonsChange}
        style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 8 }}
      />
      {state.selectedReasons.includes('other') && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Input.TextArea
            rows={3}
            onChange={actions.handleOtherReasonChange}
            value={state.otherReason}
            placeholder={translate('accountDetail.disableLicence.reasonStep.otherPlaceholder')}
          />
        </div>
      )}

      <AppButton
        text={translate('accountDetail.disableLicence.continueButton')}
        action={actions.handleContinuar}
        disabled={
          state.selectedReasons.length === 0 ||
          (state.selectedReasons.length === 1 &&
            state.selectedReasons[0] === 'other' &&
            !state.otherReason.trim())
        }
      ></AppButton>
    </form>
  );

  const StepSelectServices = (
    <Spin
      spinning={state.isLicencesLoading}
      tip={translate('accountDetail.disableLicence.serviceSelectionStep.loadingLicences')}
    >
      <Row justify="center" align="top" style={{ margin: '0 auto', maxWidth: 600 }}>
        <Col span={24} style={{ marginBottom: 82 }}>
          <label>{translate('accountDetail.disableLicence.serviceSelectionStep.subtitle')}</label>
        </Col>
      </Row>

      <Row justify="center" align="top">
        <Col span={24} style={{ marginBottom: 16 }}>
          <HotelSelector
            enableAllHotels={true}
            darkTheme={true}
            onHotelSelected={actions.handleHotelSelected}
          ></HotelSelector>
        </Col>
      </Row>
      <Row>
        {state.isAllHotelsSelected ? (
          <Col span={24} style={{ marginBottom: 16 }}>
            <Checkbox
              checked={state.isAllServicesCheckbox}
              onChange={actions.handleAllServicesCheckbox}
            >
              {translate('accountDetail.disableLicence.serviceSelectionStep.allServicesCheckbox')}
            </Checkbox>
          </Col>
        ) : (
          <Col span={24} style={{ marginBottom: 16 }}>
            {(() => {
              if (state.fetchLicencesError) {
                return (
                  <label style={{ color: 'red', fontWeight: 'bold' }}>
                    {translate(
                      'accountDetail.disableLicence.serviceSelectionStep.fetchLicencesError'
                    )}
                  </label>
                );
              } else if (state.hotelLicences.length === 0 && !state.isLicencesLoading) {
                return (
                  <label style={{ fontWeight: 'bold' }}>
                    {translate('accountDetail.disableLicence.serviceSelectionStep.noLicencesFound')}
                  </label>
                );
              } else {
                return (
                  <form
                    style={{
                      width: '100%',
                      minWidth: 400,
                      maxWidth: 600,
                      margin: '0 auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 20,
                    }}
                  >
                    <Checkbox.Group
                      value={state.selectedHotelLicences}
                      onChange={actions.handleHotelLicencesChange}
                      style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                    >
                      {state.hotelLicences.map(licence => {
                        const isDisabled = [
                          constants.LICENCE_END_STATUS.REQUESTED,
                          constants.LICENCE_END_STATUS.APPROVED,
                        ].includes(licence.endStatus);

                        return (
                          <label
                            key={licence.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '8px 12px',
                              border: '1px solid #f0f0f0',
                              borderRadius: 6,
                              backgroundColor: isDisabled ? '#fafafa' : '#fff',
                              width: '100%',
                              boxSizing: 'border-box',
                              cursor: isDisabled ? 'not-allowed' : 'pointer',
                              gap: 12,
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                flexGrow: 1,
                              }}
                            >
                              <Checkbox
                                value={licence.id}
                                disabled={isDisabled}
                                style={{ margin: 0 }}
                              />
                              <img
                                src={getLicenceIconUrl(licence.name)}
                                alt={licence.name}
                                style={{ width: 32, height: 32, objectFit: 'contain' }}
                              />
                              <div style={{ flexGrow: 1 }}>
                                <div style={{ fontWeight: 500, fontSize: 14 }}>
                                  L{licence.id} -{' '}
                                  {translate('billingConfig.licence.types.' + licence.name)}
                                </div>
                                <div style={{ fontSize: 12, color: '#888' }}>
                                  {licence.endStatus === constants.LICENCE_END_STATUS.APPROVED
                                    ? translate(
                                        'accountDetail.disableLicence.serviceSelectionStep.endDate'
                                      )
                                    : translate(
                                        'accountDetail.disableLicence.serviceSelectionStep.renewalDate'
                                      )}
                                  :{' '}
                                  {new Date(
                                    licence.renewalDate || licence.endDate
                                  ).toLocaleDateString('es-ES', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                  })}
                                </div>
                              </div>
                            </div>

                            {licence.endStatus === constants.LICENCE_END_STATUS.REQUESTED && (
                              <Popover
                                title={translate(
                                  'accountDetail.disableLicence.serviceSelectionStep.servicePending'
                                )}
                                content={
                                  <span>
                                    {translate(
                                      'accountDetail.disableLicence.serviceSelectionStep.serviceNotChoosable'
                                    )}{' '}
                                    <b>
                                      {new Date(licence.endRequestDate).toLocaleDateString(
                                        'es-ES',
                                        {
                                          day: '2-digit',
                                          month: '2-digit',
                                          year: 'numeric',
                                        }
                                      )}
                                    </b>
                                  </span>
                                }
                              >
                                <InfoCircleOutlined style={{ fontSize: 18, color: '#1890ff' }} />
                              </Popover>
                            )}

                            {licence.endStatus === constants.LICENCE_END_STATUS.APPROVED && (
                              <Popover
                                title={translate(
                                  'accountDetail.disableLicence.serviceSelectionStep.serviceAccepted'
                                )}
                                content={
                                  <span>
                                    {translate(
                                      'accountDetail.disableLicence.serviceSelectionStep.serviceAcceptedDescription'
                                    )}
                                  </span>
                                }
                              >
                                <CheckCircleOutlined style={{ fontSize: 18, color: '#52c41a' }} />
                              </Popover>
                            )}
                          </label>
                        );
                      })}
                    </Checkbox.Group>
                  </form>
                );
              }
            })()}
          </Col>
        )}
      </Row>
      <Row justify="space-between" align="top">
        <Col span={10}>
          <AppButton
            text={translate('accountDetail.disableLicence.backButton')}
            action={actions.handleBack}
            secondary={true}
          ></AppButton>
        </Col>
        <Col span={10}>
          <AppButton
            text={translate('accountDetail.disableLicence.continueButton')}
            action={actions.handleContinuar}
            disabled={
              state.isAllHotelsSelected
                ? state.isAllServicesCheckbox === false
                : Object.keys(state.selectedLicencesWithHotelId).length === 0
            }
          ></AppButton>
        </Col>
      </Row>
    </Spin>
  );

  const StepSummary = (
    <Spin
      spinning={state.isConfirmLoading}
      tip={translate('accountDetail.disableLicence.summaryStep.loading')}
    >
      <Row style={{ marginBottom: 32 }}>
        <Col span={24}>
          <label>{translate('accountDetail.disableLicence.summaryStep.reasonsListSubtitle')}</label>
          <List
            style={{ marginBottom: 12 }}
            size="small"
            bordered
            dataSource={state.selectedReasons}
            renderItem={item => {
              if (item === 'other' && state.otherReason.trim() !== '') {
                return (
                  <List.Item>
                    <span>
                      {translate(`accountDetail.disableLicence.reasonStep.reasons.other`) +
                        ': ' +
                        state.otherReason.trim()}
                    </span>
                  </List.Item>
                );
              } else {
                return (
                  <List.Item>
                    {translate(`accountDetail.disableLicence.reasonStep.reasons.${item}`)}
                  </List.Item>
                );
              }
            }}
          />
        </Col>
      </Row>

      <Row style={{ marginBottom: 16 }}>
        {state.isAllHotelsSelected && state.isAllServicesCheckbox ? (
          <Col span={24}>
            <label>
              {translate('accountDetail.disableLicence.summaryStep.allServicesSelected')}
            </label>
          </Col>
        ) : (
          <Col span={24}>
            <label>
              {translate('accountDetail.disableLicence.summaryStep.licencesListSubtitle')}
            </label>
            {Object.entries(state.selectedLicencesWithHotelId).map(([hotelId, licenceIds]) => {
              const hotel = appState.hotelList?.find(h => String(h.id) === String(hotelId));
              const hotelName = hotel ? hotel.name : hotelId;

              const metaForHotel = state.selectedLicencesMetaWithHotelId?.[hotelId] || {};

              const displayItems = (licenceIds || []).map(licenceId => {
                const meta =
                  metaForHotel[licenceId] ||
                  (state.hotelLicences || []).find(l => String(l.id) === String(licenceId));

                const licenceName = meta && meta.name ? meta.name : null;

                const label = licenceName
                  ? `L${licenceId} - ${translate('billingConfig.licence.types.' + licenceName)}`
                  : `L${licenceId}`;

                return label;
              });

              return (
                <List
                  key={hotelId}
                  style={{ marginBottom: 12 }}
                  size="small"
                  header={hotelName}
                  bordered
                  dataSource={displayItems}
                  renderItem={item => <List.Item>{item}</List.Item>}
                />
              );
            })}
          </Col>
        )}
      </Row>

      <Row justify="space-between" align="top">
        <Col span={10}>
          <AppButton
            text={translate('accountDetail.disableLicence.backButton')}
            action={actions.handleBack}
            secondary={true}
          ></AppButton>
        </Col>
        <Col span={10}>
          <AppButton
            text={translate('accountDetail.disableLicence.confirmButton')}
            action={actions.handleConfirm}
          ></AppButton>
        </Col>
      </Row>
    </Spin>
  );

  const ErrorResult = (
    <Result
      status="error"
      title={translate('accountDetail.disableLicence.errorResult.title')}
      subTitle={translate('accountDetail.disableLicence.errorResult.subtitle')}
    />
  );

  const SuccessResult = (
    <Result
      status="info"
      title={translate('accountDetail.disableLicence.successResult.title')}
      subTitle={translate('accountDetail.disableLicence.successResult.subtitle')}
    />
  );

  const renderStepContent = step => {
    switch (step) {
      case 0:
        return StepReason;
      case 1:
        return StepSelectServices;
      case 2:
        return StepSummary;
      case 3:
        return ErrorResult;
      case 4:
        return SuccessResult;
      default:
        return null;
    }
  };

  return (
    <div style={styles.wizardContainer}>
      <div style={styles.contentArea}>
        <h2 style={styles.stepTitle}>{steps[currentStep]}</h2>
        <div style={styles.stepContent}>{renderStepContent(currentStep)}</div>
      </div>
      <div style={{ ...styles.stepIndicators, padding: 0 }}>
        {![3, 4].includes(state.step) && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
            {steps.map((stepTitle, idx) => {
              const isActive = idx === currentStep;
              return (
                <button
                  key={stepTitle}
                  type="button"
                  aria-current={isActive ? 'step' : undefined}
                  aria-label={`Step ${idx + 1}: ${stepTitle}`}
                  tabIndex={isActive ? 0 : -1}
                  disabled={!isActive}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: isActive ? '#1976d2' : '#e0e0e0',
                    color: isActive ? '#fff' : '#333',
                    fontWeight: isActive ? 'bold' : 'normal',
                    fontSize: 18,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'default',
                    boxShadow: isActive ? '0 2px 8px rgba(25,118,210,0.15)' : 'none',
                    border: isActive ? '2px solid #1976d2' : '2px solid #e0e0e0',
                    transition: 'all 0.2s',
                    outline: 'none',
                  }}
                >
                  {idx + 1}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
});

export default DisableLicenceForm;
